import { Link, useParams } from "react-router-dom";
import {
  ArrowLeft,
  BookOpen,
  Sparkles,
  Wrench,
  AlertTriangle,
  CheckCircle2,
  PlayCircle,
  Clock3,
  Layers3,
  Hammer,
  ShieldCheck,
  Link2,
  Gauge,
} from "lucide-react";
import howtoData from "../data/howto.json";

type HowToStep = {
  title: string;
  babySteps?: string[];
};

type HowToVideo = {
  label: string;
  url: string;
  note?: string;
};

function BluePanel({
  children,
  className = "",
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div
      className={[
        "relative overflow-hidden rounded-[28px] border border-dtf-accent/20",
        "bg-[radial-gradient(circle_at_top_center,rgba(37,99,235,0.18),transparent_42%),linear-gradient(180deg,rgba(7,16,35,0.98)_0%,rgba(6,14,30,0.98)_100%)]",
        "shadow-[0_0_0_1px_rgba(245,158,11,0.05),0_0_34px_rgba(37,99,235,0.16)]",
        className,
      ].join(" ")}
    >
      <div className="pointer-events-none absolute inset-0 bg-[linear-gradient(90deg,rgba(37,99,235,0.08),transparent_30%,transparent_70%,rgba(37,99,235,0.06))]" />
      <div className="pointer-events-none absolute inset-0 rounded-[28px] ring-1 ring-inset ring-white/5" />
      {children}
    </div>
  );
}

function SectionCard({
  title,
  icon,
  children,
}: {
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <BluePanel className="mb-6">
      <div className="border-b border-white/8 px-6 py-5">
        <div className="flex items-center gap-3">
          <div className="flex h-11 w-11 items-center justify-center rounded-2xl border border-dtf-accent/20 bg-black/20 text-dtf-accent shadow-[0_0_14px_rgba(37,99,235,0.20)]">
            {icon}
          </div>
          <h2 className="text-2xl font-bold tracking-tight text-white">{title}</h2>
        </div>
      </div>

      <div className="px-6 py-6">{children}</div>
    </BluePanel>
  );
}

function MetaChip({ children }: { children: React.ReactNode }) {
  return (
    <span className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/20 px-3 py-1.5 text-xs font-medium text-dtf-text-muted">
      {children}
    </span>
  );
}

function BulletList({ items }: { items?: string[] }) {
  if (!items?.length) return null;

  return (
    <ul className="space-y-4">
      {items.map((item, index) => (
        <li key={`${item}-${index}`} className="flex items-start gap-4">
          <span className="mt-2.5 h-2.5 w-2.5 flex-shrink-0 rounded-full bg-dtf-accent shadow-[0_0_10px_rgba(245,158,11,0.45)]" />
          <span className="text-dtf-text text-[16px] leading-7">{item}</span>
        </li>
      ))}
    </ul>
  );
}

function StepList({ steps }: { steps?: HowToStep[] }) {
  if (!steps?.length) return null;

  return (
    <div className="space-y-5">
      {steps.map((step, index) => (
        <div
          key={`${step.title}-${index}`}
          className="rounded-[24px] border border-white/10 bg-black/18 p-5 shadow-[0_0_18px_rgba(37,99,235,0.08)]"
        >
          <div className="mb-4 flex items-start gap-4">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-dtf-blue/15 text-sm font-bold text-dtf-blue shadow-[0_0_12px_rgba(59,130,246,0.22)]">
              {index + 1}
            </div>

            <div className="min-w-0">
              <h3 className="text-xl font-semibold text-white">
                Step {index + 1}: {step.title}
              </h3>
            </div>
          </div>

          {step.babySteps?.length ? (
            <div className="pl-[52px]">
              <BulletList items={step.babySteps} />
            </div>
          ) : null}
        </div>
      ))}
    </div>
  );
}

function getEmbedUrl(url: string) {
  try {
    const parsed = new URL(url);

    if (parsed.hostname.includes("youtube.com")) {
      const videoId = parsed.searchParams.get("v");
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }

    if (parsed.hostname.includes("youtu.be")) {
      const videoId = parsed.pathname.replace("/", "").split("?")[0];
      return videoId ? `https://www.youtube.com/embed/${videoId}` : url;
    }

    return url;
  } catch {
    return url;
  }
}

export default function HowToDetail() {
  const { id } = useParams();
  const howto = howtoData.howtos.find((h) => h.id === id);

  if (!howto) {
    return (
      <div className="mx-auto max-w-5xl px-4 py-10">
        <BluePanel>
          <div className="p-8 text-center">
            <h1 className="mb-3 text-3xl font-bold text-white">Guide not found</h1>
            <p className="mb-6 text-dtf-text-muted">
              The guide you tried to open does not exist or may have been removed.
            </p>
            <Link
              to="/howto"
              className="inline-flex items-center gap-2 rounded-2xl bg-dtf-accent px-5 py-3 font-semibold text-dtf-dark transition hover:bg-dtf-accent-hover"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to How-To
            </Link>
          </div>
        </BluePanel>
      </div>
    );
  }

  const relatedGuides = (howto.relatedGuides ?? [])
    .map((guideId) => howtoData.howtos.find((h) => h.id === guideId))
    .filter(Boolean);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8">
      <Link
        to="/howto"
        className="mb-6 inline-flex items-center gap-2 text-dtf-text-muted transition hover:text-white"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to How-To
      </Link>

      <BluePanel className="mb-6">
        <div className="p-6 sm:p-7">
          <div className="flex flex-col gap-5">
            <div className="flex flex-wrap gap-2">
              <span className="inline-flex rounded-full border border-dtf-accent/20 bg-black/20 px-3 py-1 text-xs font-semibold uppercase tracking-[0.16em] text-dtf-accent">
                {howto.category}
              </span>

              {howto.level && (
                <span className="inline-flex rounded-full border border-dtf-blue/20 bg-dtf-blue/10 px-3 py-1 text-xs font-medium capitalize text-dtf-blue">
                  {howto.level}
                </span>
              )}

              {howto.timeEstimate && (
                <MetaChip>
                  <Clock3 className="h-3.5 w-3.5 text-dtf-accent" />
                  {howto.timeEstimate}
                </MetaChip>
              )}

              {howto.steps?.length ? (
                <MetaChip>
                  <Layers3 className="h-3.5 w-3.5 text-dtf-blue" />
                  {howto.steps.length} steps
                </MetaChip>
              ) : null}
            </div>

            <div>
              <h1 className="mb-3 text-4xl font-extrabold tracking-tight text-white sm:text-5xl">
                {howto.title}
              </h1>

              {howto.summary && (
                <p className="max-w-4xl text-lg leading-8 text-dtf-text">
                  {howto.summary}
                </p>
              )}
            </div>
          </div>
        </div>
      </BluePanel>

      {howto.wizardTip && (
        <SectionCard title="Wizard Tip" icon={<Sparkles className="h-5 w-5" />}>
          <div className="rounded-[24px] border border-dtf-accent/15 bg-black/20 p-5">
            <p className="text-dtf-text leading-8">{howto.wizardTip}</p>
          </div>
        </SectionCard>
      )}

      {(howto.symptoms?.length ?? 0) > 0 && (
        <SectionCard title="Common Symptoms" icon={<Gauge className="h-5 w-5" />}>
          <BulletList items={howto.symptoms} />
        </SectionCard>
      )}

      {(howto.tools?.length ?? 0) > 0 && (
        <SectionCard title="Tools Needed" icon={<Hammer className="h-5 w-5" />}>
          <BulletList items={howto.tools} />
        </SectionCard>
      )}

      {(howto.beforeYouStart?.length ?? 0) > 0 && (
        <SectionCard title="Before You Start" icon={<ShieldCheck className="h-5 w-5" />}>
          <BulletList items={howto.beforeYouStart} />
        </SectionCard>
      )}

      {(howto.steps?.length ?? 0) > 0 && (
        <SectionCard title="Step-by-Step Guide" icon={<BookOpen className="h-5 w-5" />}>
          <StepList steps={howto.steps as HowToStep[]} />
        </SectionCard>
      )}

      {(howto.commonMistakes?.length ?? 0) > 0 && (
        <SectionCard title="Common Mistakes" icon={<AlertTriangle className="h-5 w-5" />}>
          <BulletList items={howto.commonMistakes} />
        </SectionCard>
      )}

      {(howto.troubleshooting?.length ?? 0) > 0 && (
        <SectionCard title="Troubleshooting" icon={<Wrench className="h-5 w-5" />}>
          <BulletList items={howto.troubleshooting} />
        </SectionCard>
      )}

      {(howto.proTips?.length ?? 0) > 0 && (
        <SectionCard title="Pro Tips" icon={<CheckCircle2 className="h-5 w-5" />}>
          <BulletList items={howto.proTips} />
        </SectionCard>
      )}

      {(howto.videoLinks?.length ?? 0) > 0 && (
        <SectionCard title="Video Guides" icon={<PlayCircle className="h-5 w-5" />}>
          <div className="space-y-6">
            {(howto.videoLinks as HowToVideo[]).map((video, index) => (
              <div
                key={`${video.label}-${index}`}
                className="rounded-[24px] border border-white/10 bg-black/18 p-4 shadow-[0_0_18px_rgba(37,99,235,0.08)]"
              >
                <div className="mb-3 text-lg font-semibold text-white">{video.label}</div>

                <iframe
                  src={getEmbedUrl(video.url)}
                  className="aspect-video w-full rounded-xl border border-white/10 bg-black"
                  allowFullScreen
                  title={video.label}
                />

                {video.note && (
                  <p className="mt-3 text-sm leading-7 text-dtf-text-muted">
                    {video.note}
                  </p>
                )}
              </div>
            ))}
          </div>
        </SectionCard>
      )}

      {relatedGuides.length > 0 && (
        <SectionCard title="Related Guides" icon={<Link2 className="h-5 w-5" />}>
          <div className="flex flex-wrap gap-3">
            {relatedGuides.map((related) => {
              if (!related) return null;

              return (
                <Link
                  key={related.id}
                  to={`/howto/${related.id}`}
                  className="rounded-2xl border border-white/10 bg-black/20 px-4 py-3 text-dtf-text transition hover:border-dtf-blue/30 hover:bg-black/30 hover:text-white"
                >
                  {related.title}
                </Link>
              );
            })}
          </div>
        </SectionCard>
      )}
    </div>
  );
}